import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ALL_FILTER_VALUE,
  AnalyticsFilter,
  AyotResponse,
  BlueprintResponse,
  CoachingResponse,
  EngagementResponse,
  FilterOptionsResponse,
  LifeReadinessResponse,
  OverviewResponse,
  ToolUsageResponse,
} from './coachingAnalytics.types';
import { baseQueryWithAuth } from '../baseQuery';

/**
 * Filters travel as one JSON `filter` param. Unset and "all" selections are
 * dropped rather than sent, so the query key for an un-narrowed view is the
 * same however the bar got there.
 */
export type AnalyticsQuery = Readonly<{
  filter?: AnalyticsFilter;
}>;

const buildSectionPath = (section: string, { filter }: AnalyticsQuery) => {
  const selected = Object.entries(filter ?? {}).filter(
    ([, value]) => value != null && value !== '' && value !== ALL_FILTER_VALUE,
  );

  if (!selected.length) return `coaching-analytics/${section}`;

  const params = new URLSearchParams({ filter: JSON.stringify(Object.fromEntries(selected)) });

  return `coaching-analytics/${section}?${params.toString()}`;
};

/**
 * The seven dashboard sections, each an independent query so a slow section
 * cannot hold up the rest of the page, plus the filter bar's own options.
 *
 * Read-only, so there is nothing to invalidate: each section is cached against
 * its filter arguments and refetches when they change. Every section is a
 * cohort-wide aggregate, so cache entries are held for an hour rather than the
 * 60-second default.
 */
export const coachingAnalyticsApi = createApi({
  reducerPath: 'coachingAnalyticsApi',
  baseQuery: baseQueryWithAuth,
  keepUnusedDataFor: 60 * 60,
  endpoints: (build) => ({
    getCoachingAnalyticsFilterOptions: build.query<FilterOptionsResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('filters', args),
    }),
    getCoachingAnalyticsOverview: build.query<OverviewResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('overview', args),
    }),
    getCoachingAnalyticsBlueprint: build.query<BlueprintResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('blueprint', args),
    }),
    getCoachingAnalyticsAyot: build.query<AyotResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('ayot', args),
    }),
    getCoachingAnalyticsLifeReadiness: build.query<LifeReadinessResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('life-readiness', args),
    }),
    getCoachingAnalyticsEngagement: build.query<EngagementResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('engagement', args),
    }),
    getCoachingAnalyticsCoaching: build.query<CoachingResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('coaching', args),
    }),
    getCoachingAnalyticsToolUsage: build.query<ToolUsageResponse, AnalyticsQuery>({
      query: (args) => buildSectionPath('tool-usage', args),
    }),
  }),
});

export const {
  useGetCoachingAnalyticsFilterOptionsQuery,
  useGetCoachingAnalyticsOverviewQuery,
  useGetCoachingAnalyticsBlueprintQuery,
  useGetCoachingAnalyticsAyotQuery,
  useGetCoachingAnalyticsLifeReadinessQuery,
  useGetCoachingAnalyticsEngagementQuery,
  useGetCoachingAnalyticsCoachingQuery,
  useGetCoachingAnalyticsToolUsageQuery,
} = coachingAnalyticsApi;
